/**
 * Social Sharing Service
 * Handles sharing to Twitter, Discord, and Telegram with pre-generated social cards
 */

export interface ShareOptions {
  text: string;
  url?: string;
  imageData?: string; // Base64 data URL
  hashtags?: string[];
}

/**
 * Share to Twitter (X)
 * Opens Twitter share dialog with text and hashtags
 */
export const shareToTwitter = async (options: ShareOptions): Promise<void> => {
  const { text, url, hashtags = ['Signaly', 'TradingSignals', 'CryptoTrading'] } = options;
  
  const twitterUrl = new URL('https://twitter.com/intent/tweet');
  twitterUrl.searchParams.append('text', text);
  
  if (url) {
    twitterUrl.searchParams.append('url', url);
  }
  
  if (hashtags.length > 0) {
    twitterUrl.searchParams.append('hashtags', hashtags.join(','));
  }

  // Open in new window
  window.open(twitterUrl.toString(), '_blank', 'width=600,height=400');
};

/**
 * Share to Discord
 * Creates a shareable message with webhook or clipboard copy
 */
export const shareToDiscord = async (options: ShareOptions): Promise<void> => {
  const { text, imageData } = options;
  
  // Create formatted Discord message
  const discordMessage = `🚀 **Signaly Trading Alert**\n\n${text}\n\nPowered by Signaly AI`;

  if (imageData) {
    // If image data is available, download it
    try {
      await downloadImage(imageData, 'signaly-share.png');
      
      // Copy message to clipboard
      await navigator.clipboard.writeText(discordMessage);
      
      alert('📋 Message copied to clipboard!\n📸 Image downloaded!\n\nPaste both in Discord to share your achievement.');
    } catch (error) {
      console.error('Error sharing to Discord:', error);
      alert('Failed to prepare Discord share. Please try again.');
    }
  } else {
    // Just copy text to clipboard
    try {
      await navigator.clipboard.writeText(discordMessage);
      alert('📋 Message copied to clipboard! Paste in Discord to share.');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy message. Please try again.');
    }
  }
};

/**
 * Share to Telegram
 * Opens Telegram share dialog with text
 */
export const shareToTelegram = async (options: ShareOptions): Promise<void> => {
  const { text, url } = options;
  
  let telegramText = `🚀 ${text}`;
  
  if (url) {
    telegramText += `\n\n${url}`;
  }
  
  telegramText += '\n\nPowered by Signaly AI 🎯';
  
  const telegramUrl = new URL('https://t.me/share/url');
  telegramUrl.searchParams.append('text', telegramText);
  
  if (url) {
    telegramUrl.searchParams.append('url', url);
  }

  // Open in new window
  window.open(telegramUrl.toString(), '_blank', 'width=600,height=400');
};

/**
 * Download image from data URL
 */
const downloadImage = async (dataUrl: string, filename: string): Promise<void> => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Share signal to multiple platforms
 */
export const shareSignal = async (
  signal: {
    pair: string;
    type: 'BUY' | 'SELL';
    confidence: number;
    profit?: number;
  },
  platform: 'twitter' | 'discord' | 'telegram',
  imageData?: string
): Promise<void> => {
  const profitText = signal.profit != null
    ? ` | ${signal.profit >= 0 ? '✅' : '❌'} ${signal.profit >= 0 ? '+' : ''}${signal.profit.toFixed(2)}%`
    : '';

  const text = `${signal.type === 'BUY' ? '📈' : '📉'} ${signal.type} Signal Alert: ${signal.pair} | ${signal.confidence}% Confidence${profitText}`;

  const siteUrl = typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SITE_URL 
    ? process.env.NEXT_PUBLIC_SITE_URL 
    : (typeof window !== 'undefined' ? window.location.origin : 'https://signaly.me');

  const shareOptions: ShareOptions = {
    text,
    url: siteUrl,
    imageData,
    hashtags: ['TradingSignals', signal.pair.replace('/', ''), 'CryptoTrading'],
  };

  switch (platform) {
    case 'twitter':
      await shareToTwitter(shareOptions);
      break;
    case 'discord':
      await shareToDiscord(shareOptions);
      break;
    case 'telegram':
      await shareToTelegram(shareOptions);
      break;
  }
};

/**
 * Share portfolio achievement to multiple platforms
 */
export const sharePortfolioAchievement = async (
  portfolio: {
    totalValue: number;
    totalProfitLoss: number;
    totalProfitLossPercentage: number;
  },
  platform: 'twitter' | 'discord' | 'telegram',
  imageData?: string
): Promise<void> => {
  const emoji = portfolio.totalProfitLossPercentage >= 0 ? '🚀' : '📊';
  const text = `${emoji} Portfolio Update: $${portfolio.totalValue.toLocaleString()} | ${portfolio.totalProfitLossPercentage >= 0 ? '+' : ''}${portfolio.totalProfitLossPercentage.toFixed(2)}% Return`;

  const siteUrl = typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SITE_URL 
    ? process.env.NEXT_PUBLIC_SITE_URL 
    : (typeof window !== 'undefined' ? window.location.origin : 'https://signaly.me');

  const shareOptions: ShareOptions = {
    text,
    url: siteUrl,
    imageData,
    hashtags: ['Portfolio', 'Trading', 'Investment'],
  };

  switch (platform) {
    case 'twitter':
      await shareToTwitter(shareOptions);
      break;
    case 'discord':
      await shareToDiscord(shareOptions);
      break;
    case 'telegram':
      await shareToTelegram(shareOptions);
      break;
  }
};

/**
 * Share milestone to multiple platforms
 */
export const shareMilestone = async (
  milestone: {
    title: string;
    description: string;
  },
  platform: 'twitter' | 'discord' | 'telegram',
  imageData?: string
): Promise<void> => {
  const text = `🏆 ${milestone.title}: ${milestone.description}`;

  const shareOptions: ShareOptions = {
    text,
    url: typeof window !== 'undefined' ? window.location.origin : undefined,
    imageData,
    hashtags: ['Achievement', 'Trading', 'Milestone'],
  };

  switch (platform) {
    case 'twitter':
      await shareToTwitter(shareOptions);
      break;
    case 'discord':
      await shareToDiscord(shareOptions);
      break;
    case 'telegram':
      await shareToTelegram(shareOptions);
      break;
  }
};